import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { AgentConnection, AgentConnectionRequest } from "@/types";

function useConnClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidatePair(qc: ReturnType<typeof useQueryClient>) {
  return (agentAId: string, agentBId: string) => {
    qc.invalidateQueries({ queryKey: queryKeys.detail("agents", agentAId) });
    qc.invalidateQueries({ queryKey: queryKeys.detail("agents", agentBId) });
  };
}

export function useRequestConnection(): UseMutationResult<
  AgentConnectionRequest,
  Error,
  { requesterAgentId: string; targetAgentId: string; reason?: string | null }
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePair(qc);
  const getClient = useConnClient();
  return useMutation({
    mutationFn: ({ requesterAgentId, targetAgentId, reason }) =>
      getClient().post<AgentConnectionRequest>(
        `/agents/${requesterAgentId}/connection-requests`,
        { targetAgentId, reason: reason ?? null },
      ),
    onSuccess: (req) => invalidate(req.requesterAgentId, req.targetAgentId),
  });
}

export function useAcceptConnectionRequest(): UseMutationResult<
  { request: AgentConnectionRequest; connection: AgentConnection },
  Error,
  string
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePair(qc);
  const getClient = useConnClient();
  return useMutation({
    mutationFn: (id) =>
      getClient().post<{ request: AgentConnectionRequest; connection: AgentConnection }>(
        `/connection-requests/${id}/accept`,
      ),
    onSuccess: ({ connection }) => invalidate(connection.agentAId, connection.agentBId),
  });
}

export function useRejectConnectionRequest(): UseMutationResult<
  AgentConnectionRequest,
  Error,
  string
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePair(qc);
  const getClient = useConnClient();
  return useMutation({
    mutationFn: (id) =>
      getClient().post<AgentConnectionRequest>(`/connection-requests/${id}/reject`),
    onSuccess: (req) => invalidate(req.requesterAgentId, req.targetAgentId),
  });
}

export function useCancelConnectionRequest(): UseMutationResult<
  { ok: true },
  Error,
  { requestId: string; requesterAgentId: string; targetAgentId: string }
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePair(qc);
  const getClient = useConnClient();
  return useMutation({
    mutationFn: ({ requestId }) =>
      getClient().delete<{ ok: true }>(`/connection-requests/${requestId}`),
    onSuccess: (_data, vars) => invalidate(vars.requesterAgentId, vars.targetAgentId),
  });
}

export function useCreateDirectConnection(): UseMutationResult<
  AgentConnection,
  Error,
  { agentAId: string; agentBId: string }
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePair(qc);
  const getClient = useConnClient();
  return useMutation({
    mutationFn: (body) => getClient().post<AgentConnection>(`/connections`, body),
    onSuccess: (conn) => invalidate(conn.agentAId, conn.agentBId),
  });
}

export function useRevokeConnection(): UseMutationResult<
  AgentConnection,
  Error,
  { connectionId: string; agentAId: string; agentBId: string }
> {
  const qc = useQueryClient();
  const invalidate = useInvalidatePair(qc);
  const getClient = useConnClient();
  return useMutation({
    mutationFn: ({ connectionId }) =>
      getClient().delete<AgentConnection>(`/connections/${connectionId}`),
    onSuccess: (_data, vars) => invalidate(vars.agentAId, vars.agentBId),
  });
}
